import React, { useState } from "react";
import TableField from "./TableField";
import MatrixField from "./MatrixField";
import { StickyNote, X } from "lucide-react";

// Word-counting mirror of backend `scoring._words` — used by long_text fields
// to render the same live count the server will score against. Not exposed
// externally; if the two ever diverge, prefer the server's number.
function countWords(v) {
  if (typeof v !== "string") return 0;
  return (v.match(/[A-Za-z0-9][A-Za-z0-9'’\-/%$.]*/g) || []).length;
}

/**
 * FieldNote — private per-field scratchpad for reasoning trails.
 * Never appears in the PDF, never surfaced to admins. Autosaves through the
 * same merge PUT via `onChangeNote`. Collapsible so the form stays clean.
 */
function FieldNote({ fieldId, value, onChange, onBlur }) {
  // `open` tracks whether the note panel is currently shown. We start it
  // open when the field already has a saved note, so the user doesn't
  // have to re-expand it on every load.
  const [open, setOpen] = useState(!!value);
  // `userOpened` is true only when the current open-state was triggered
  // by the user clicking "Add note to self" (not by pre-existing saved
  // content). We only `autoFocus` in that case — otherwise, the moment
  // the worksheet loads, the browser scrolls the first filled note into
  // view and pre-selects its radios/inputs, which is confusing on long
  // sheets like Founder Equity & Control Architecture.
  const [userOpened, setUserOpened] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setUserOpened(true); setOpen(true); }}
        className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition"
        data-testid={`field-note-toggle-${fieldId}`}
      >
        <StickyNote className="w-3 h-3" /> Add note to self
      </button>
    );
  }
  return (
    <div className="mt-2 rounded-md border border-pink-300/50 bg-pink-50/70 p-2.5">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-pink-800 font-semibold inline-flex items-center gap-1">
          <StickyNote className="w-3 h-3" /> Private note
        </div>
        <button
          type="button"
          onClick={() => {
            if (value) onChange("");
            setOpen(false);
            setUserOpened(false);
            onBlur && onBlur();
          }}
          className="text-pink-800/70 hover:text-pink-900"
          aria-label="Close note"
          data-testid={`field-note-close-${fieldId}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <textarea
        rows={2}
        autoFocus={userOpened}
        placeholder="Why did you answer this way? Anyone you need to check with? Deadlines?"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full text-[13px] leading-snug bg-transparent outline-none resize-y text-pink-950 placeholder:text-pink-800/50"
        data-testid={`field-note-${fieldId}`}
      />
    </div>
  );
}

function Label({ field }) {
  const optional = field.required === false;
  return (
    <div className="mb-2">
      <label className="text-sm font-medium leading-snug text-foreground">
        {field.label}
        {optional && (
          <span className="ml-1.5 text-[11px] font-normal text-muted-foreground/70">(optional)</span>
        )}
      </label>
      {field.help && (
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{field.help}</p>
      )}
    </div>
  );
}

function ShortText({ field, value, onChange, onBlur }) {
  return (
    <input
      type="text"
      maxLength={field.max_length || undefined}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70 focus:ring-1 focus:ring-[color:var(--brand-accent,#E91E63)]/40"
      data-testid={`field-${field.field_id}`}
    />
  );
}

function LongText({ field, value, onChange, onBlur }) {
  const rows = field.rows || 4;
  const min = field.min_words || 0;
  const words = countWords(value);
  const met = min === 0 || words >= min;
  return (
    <div>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70 focus:ring-1 focus:ring-[color:var(--brand-accent,#E91E63)]/40 resize-y leading-relaxed"
        data-testid={`field-${field.field_id}`}
      />
      {min > 0 && (
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className={met ? "text-emerald-400" : "text-pink-400"}>
            {words} / {min} words
          </span>
          {!met && <span className="text-muted-foreground/70">— keep going</span>}
        </div>
      )}
    </div>
  );
}

function NumberField({ field, value, onChange, onBlur }) {
  return (
    <div className="relative w-full max-w-xs">
      <input
        type="number"
        step="any"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        onBlur={onBlur}
        className="w-full rounded-md border border-border bg-white px-3 py-2 pr-14 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70"
        data-testid={`field-${field.field_id}`}
      />
      {field.unit && (
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{field.unit}</span>
      )}
    </div>
  );
}

function CurrencyField({ field, value, onChange, onBlur }) {
  return (
    <div className="relative w-full max-w-xs">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
      <input
        type="number"
        step="any"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        onBlur={onBlur}
        className="w-full rounded-md border border-border bg-white pl-7 pr-3 py-2 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70"
        data-testid={`field-${field.field_id}`}
      />
    </div>
  );
}

function PercentField({ field, value, onChange, onBlur }) {
  return (
    <div className="relative w-full max-w-xs">
      <input
        type="number"
        step="any"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        onBlur={onBlur}
        className="w-full rounded-md border border-border bg-white px-3 py-2 pr-8 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70"
        data-testid={`field-${field.field_id}`}
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
    </div>
  );
}

function DateField({ field, value, onChange, onBlur }) {
  return (
    <input
      type="date"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70"
      data-testid={`field-${field.field_id}`}
    />
  );
}

function UrlField({ field, value, onChange, onBlur }) {
  return (
    <input
      type="url"
      placeholder="https://…"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70"
      data-testid={`field-${field.field_id}`}
    />
  );
}

function SingleSelect({ field, value, onChange, onBlur }) {
  const options = field.options || [];
  const isPreset = options.includes(value);
  const showOther = field.allow_other && value != null && value !== "" && !isPreset;
  // `otherFocus` is true only when the user just clicked the "Other"
  // radio in this session. On initial render we never auto-focus the
  // Other text input, because that would scroll the viewport straight
  // to the first pre-filled "Other" field (confusing on long worksheets
  // like Founder Equity & Control Architecture — the page would open
  // 2000+ px down the form instead of at the top).
  const [otherFocus, setOtherFocus] = React.useState(false);
  return (
    <div className="space-y-2" data-testid={`field-${field.field_id}`}>
      {options.map((opt) => {
        const id = `${field.field_id}--${opt}`;
        return (
          <label
            key={opt}
            htmlFor={id}
            className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${
              value === opt
                ? "border-[color:var(--brand-accent,#E91E63)]/60 bg-muted/50"
                : "border-border hover:border-foreground/25"
            }`}
          >
            <input
              id={id}
              type="radio"
              name={field.field_id}
              checked={value === opt}
              onChange={() => onChange(opt)}
              onBlur={onBlur}
              className="mt-1 accent-[color:var(--brand-accent,#E91E63)]"
            />
            <span className="text-sm text-foreground/90">{opt}</span>
          </label>
        );
      })}
      {field.allow_other && (
        <label
          className={`flex flex-col gap-2 rounded-md border px-3 py-2 cursor-text ${
            showOther
              ? "border-[color:var(--brand-accent,#E91E63)]/60 bg-muted/50"
              : "border-border hover:border-foreground/25"
          }`}
        >
          <div className="flex items-center gap-3">
            <input
              type="radio"
              name={field.field_id}
              checked={showOther}
              onChange={() => { setOtherFocus(true); onChange(showOther ? "" : "Other: "); }}
              className="accent-[color:var(--brand-accent,#E91E63)]"
            />
            <span className="text-sm text-foreground/90">Other</span>
          </div>
          {showOther && (
            <input
              type="text"
              autoFocus={otherFocus}
              value={value?.startsWith("Other: ") ? value.slice(7) : value}
              onChange={(e) => onChange(`Other: ${e.target.value}`)}
              onBlur={onBlur}
              placeholder="Type your answer…"
              className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70"
              data-testid={`field-${field.field_id}-other`}
            />
          )}
        </label>
      )}
    </div>
  );
}

function MultiSelect({ field, value, onChange, onBlur }) {
  const selected = Array.isArray(value) ? value : [];
  const options = field.options || [];
  const toggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter((s) => s !== opt)
      : [...selected, opt];
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid={`field-${field.field_id}`}>
      {options.map((opt) => {
        const id = `${field.field_id}--${opt}`;
        const checked = selected.includes(opt);
        return (
          <label
            key={opt}
            htmlFor={id}
            className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${
              checked
                ? "border-[color:var(--brand-accent,#E91E63)]/60 bg-muted/50"
                : "border-border hover:border-foreground/25"
            }`}
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              onChange={() => toggle(opt)}
              onBlur={onBlur}
              className="mt-1 accent-[color:var(--brand-accent,#E91E63)]"
            />
            <span className="text-sm text-foreground/90">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

function RatingField({ field, value, onChange }) {
  const min = field.scale_min ?? 1;
  const max = field.scale_max ?? 5;
  const labels = field.scale_labels || [];
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className="flex flex-wrap gap-2" data-testid={`field-${field.field_id}`}>
      {points.map((p, i) => {
        const active = value === p;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`min-w-[64px] rounded-md border px-3 py-2 text-left transition ${
              active
                ? "border-[color:var(--brand-accent,#E91E63)]/60 bg-[color:var(--brand-accent,#E91E63)]/10"
                : "border-border hover:border-foreground/40"
            }`}
            aria-pressed={active}
          >
            <div className="text-lg font-semibold leading-tight">{p}</div>
            {labels[i] && (
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{labels[i]}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FileUploadField({ field, value, onChange }) {
  const accepts = field.accepts || [];
  const acceptAttr = accepts.map((e) => (e.startsWith(".") ? e : `.${e}`)).join(",");
  return (
    <div className="space-y-2" data-testid={`field-${field.field_id}`}>
      <input
        type="file"
        accept={acceptAttr}
        onChange={(e) => {
          const f = e.target.files?.[0];
          onChange(f ? { name: f.name, size: f.size } : "");
        }}
        className="block w-full text-xs text-foreground/70 file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-secondary-foreground file:cursor-pointer hover:file:bg-secondary/85"
        data-testid={`field-${field.field_id}-file-input`}
      />
      {value && typeof value === "object" && value.name && (
        <p className="text-[11px] text-muted-foreground">Selected: {value.name}</p>
      )}
      {accepts.length > 0 && (
        <p className="text-[11px] text-muted-foreground/70">Accepts: {accepts.join(", ")}</p>
      )}
    </div>
  );
}

export default function FieldRenderer({ field, value, onChange, onBlur, highlight = false,
                                       note = "", onChangeNote, onNoteBlur }) {
  const type = field.type;
  const Body = (() => {
    switch (type) {
      case "short_text":    return ShortText;
      case "long_text":     return LongText;
      case "number":        return NumberField;
      case "currency":      return CurrencyField;
      case "percent":       return PercentField;
      case "date":          return DateField;
      case "url":           return UrlField;
      case "single_select": return SingleSelect;
      case "multi_select":  return MultiSelect;
      case "rating":        return RatingField;
      case "table":         return TableField;
      case "matrix":        return MatrixField;
      case "file_upload":   return FileUploadField;
      default: return null;
    }
  })();
  return (
    <div
      id={`f-${field.field_id}`}
      className={`space-y-2 scroll-mt-24 rounded-md transition ${
        highlight ? "ring-2 ring-pink-400/70 ring-offset-2 ring-offset-transparent" : ""
      }`}
      data-testid={`field-wrap-${field.field_id}`}
    >
      <Label field={field} />
      {Body ? (
        <Body field={field} value={value} onChange={onChange} onBlur={onBlur} />
      ) : (
        <div className="rounded-md border border-red-400/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          Unsupported field type: <code>{type}</code>
        </div>
      )}
      {onChangeNote && (
        <FieldNote
          fieldId={field.field_id}
          value={note}
          onChange={onChangeNote}
          onBlur={onNoteBlur}
        />
      )}
    </div>
  );
}
